import { PartialDeep } from 'type-fest';
import { STATUS } from '@front-app/constants/state';
import {
	Address,
	AddressResponse,
	AddressWithContactInfo,
	AddressWithContactInfoResponse,
	Base64String,
	CountryCode,
	HtmlString,
	RegionCode,
} from '../SharedModels';
import { GroupMemberListRole } from '@front-app/modules/groupMember/hooks/useFilterMemberList';

export const NULL_USER_ID = '00000000000000000000000000000000000000000000000000';

export enum UserRole {
	NONE = 'NONE',
	DEPARTEE = 'DEPARTEE',
	INVITEE = 'INVITEE',
	EXTERNAL_INVITEE = 'EXTERNAL_INVITEE',
	MEMBER = 'MEMBER',
	MODERATOR = 'MODERATOR',
	ADMINISTRATOR = 'ADMINISTRATOR',
}

// All member types that are "active" in a group (ie, invitees and departees can't do many things)
export const ACTIVE_MEMBER_ROLES = [
	UserRole.MEMBER,
	UserRole.MODERATOR,
	UserRole.ADMINISTRATOR,
];

export const INVITED_MEMBER_ROLES = [
	UserRole.INVITEE,
	UserRole.EXTERNAL_INVITEE,
];

export const enum UserDetailRoleApi {
	CSR = 'healthCare.role.customer.service.rep',
	NURSE = 'healthCare.role.nurse',
	PHYSICIAN = 'healthCare.role.physician',
	NURSE_PRACTITIONER = 'healthCare.role.nurse.practitioner',
	THERAPIST = 'healthCare.role.therapist',
	SOCIAL_SERVICE = 'healthCare.role.social.service',
	DIETITIAN = 'healthCare.role.dietitian',
	PHARMACIST = 'healthCare.role.pharmacist',
	OFFICE_ADMINISTRATOR = 'healthCare.role.office.administrator',
	OFFICE_ADMIN = 'healthCare.role.office.administrator.abbr',
	NOT_FOR_PROFIT = 'healthCare.role.not.for.profit',
	SCIENTIST = 'healthCare.role.scientist',
	COMPANY_PROFESSIONAL = 'healthCare.role.company.professional',
	INDUSTRY = 'healthCare.role.industry',
	ALL_MEMBERS = 'healthCare.role.all.members',
	OTHER = 'healthCare.role.other',
}

// These are translatable strings
export const enum UserDetailRole {
	CSR = 'healthCare.role.customer_service_rep',
	PHYSICIAN = 'healthCare.role.physician',
	NURSE = 'healthCare.role.nurse',
	NURSE_PRACTITIONER = 'healthCare.role.nurse_practitioner',
	THERAPIST = 'healthCare.role.therapist',
	SOCIAL_SERVICE = 'healthCare.role.social_service',
	DIETITIAN = 'healthCare.role.dietitian',
	PHARMACIST = 'healthCare.role.pharmacist',
	OFFICE_ADMINISTRATOR = 'healthCare.role.office_administrator',
	NOT_FOR_PROFIT = 'healthCare.role.not_for_profit',
	SCIENTIST = 'healthCare.role.scientist',
	COMPANY_PROFESSIONAL = 'healthCare.role.company_professional',
	INDUSTRY = 'healthCare.role.industry',
	ALL_MEMBERS = 'healthCare.role.all_members',
	OTHER = 'healthCare.role.other',
	NONE = '',
}

export type UserDetailRoleNoNone = Exclude<UserDetailRole, UserDetailRole.NONE>;

export const ALL_ROLES: UserDetailRoleNoNone[] = [
	UserDetailRole.PHYSICIAN,
	UserDetailRole.NURSE_PRACTITIONER,
	UserDetailRole.NURSE,
	UserDetailRole.DIETITIAN,
	UserDetailRole.OFFICE_ADMINISTRATOR,
	UserDetailRole.SOCIAL_SERVICE,
	UserDetailRole.THERAPIST,
	UserDetailRole.PHARMACIST,
	UserDetailRole.NOT_FOR_PROFIT,
	UserDetailRole.SCIENTIST,
	UserDetailRole.OTHER,
];

export const PROFESSIONAL_ROLES = [
	UserDetailRole.PHYSICIAN,
	UserDetailRole.NURSE_PRACTITIONER,
	UserDetailRole.NURSE,
	UserDetailRole.PHARMACIST,
];

export enum UserRoleApi {
	COMPANY_PROFESSIONAL = 'COMPANY_PROFESSIONAL',
	CUSTOMER_SERVICE_REP = 'CUSTOMER_SERVICE_REP',
	DIETITIAN = 'DIETITIAN',
	INDUSTRY = 'INDUSTRY',
	NOT_FOR_PROFIT = 'NOT_FOR_PROFIT',
	NURSE = 'NURSE',
	NURSE_PRACTITIONER = 'NURSE_PRACTITIONER',
	OFFICE_ADMINISTRATOR = 'OFFICE_ADMINISTRATOR',
	OTHER = 'OTHER',
	PHARMACIST = 'PHARMACIST',
	PHYSICIAN = 'PHYSICIAN',
	SCIENTIST = 'SCIENTIST',
	SOCIAL_SERVICE = 'SOCIAL_SERVICE',
	THERAPIST = 'THERAPIST',
};

export type UserClientRoleApi = Exclude<UserRoleApi, UserRoleApi.COMPANY_PROFESSIONAL | UserRoleApi.CUSTOMER_SERVICE_REP | UserRoleApi.INDUSTRY>;

export const ALL_USER_ROLES: UserClientRoleApi[] = [
	UserRoleApi.DIETITIAN,
	UserRoleApi.NOT_FOR_PROFIT,
	UserRoleApi.NURSE,
	UserRoleApi.NURSE_PRACTITIONER,
	UserRoleApi.OFFICE_ADMINISTRATOR,
	UserRoleApi.OTHER,
	UserRoleApi.PHARMACIST,
	UserRoleApi.PHYSICIAN,
	UserRoleApi.SCIENTIST,
	UserRoleApi.SOCIAL_SERVICE,
	UserRoleApi.THERAPIST,
];

export const ALL_USER_ROLE_LABELS = {
	[UserRoleApi.COMPANY_PROFESSIONAL]: 'healthCare.role.company_professional',
	[UserRoleApi.CUSTOMER_SERVICE_REP]: 'healthCare.role.customer_service_rep',
	[UserRoleApi.DIETITIAN]: 'healthCare.role.dietitian',
	[UserRoleApi.INDUSTRY]: 'healthCare.role.industry',
	[UserRoleApi.NOT_FOR_PROFIT]: 'healthCare.role.not_for_profit',
	[UserRoleApi.NURSE]: 'healthCare.role.nurse',
	[UserRoleApi.NURSE_PRACTITIONER]: 'healthCare.role.nurse_practitioner',
	[UserRoleApi.OFFICE_ADMINISTRATOR]: 'healthCare.role.office_administrator',
	[UserRoleApi.OTHER]: 'healthCare.role.other',
	[UserRoleApi.PHARMACIST]: 'healthCare.role.pharmacist',
	[UserRoleApi.PHYSICIAN]: 'healthCare.role.physician',
	[UserRoleApi.SCIENTIST]: 'healthCare.role.scientist',
	[UserRoleApi.SOCIAL_SERVICE]: 'healthCare.role.social_service',
	[UserRoleApi.THERAPIST]: 'healthCare.role.therapist',
};

export function isNotNoneRole(x: UserDetailRole | UserDetailRoleNoNone): x is UserDetailRoleNoNone {
	return x !== UserDetailRole.NONE;
}

export const enum UserGender {
	MALE = 'account.gender.male',
	FEMALE = 'account.gender.female',
}

export const enum UserSalutation {
	NONE = 'all.site.none',
	DR = 'account.salutation.dr',
	MISS = 'account.salutation.miss',
	MR = 'account.salutation.mr',
	MRS = 'account.salutation.mrs',
	MS = 'account.salutation.ms',
	PROF = 'account.salutation.prof',
}

export interface Language {
	languageId: number;
	language: string;
}

export interface Association {
	associationId: number;
	organizationName: string;
	orgType: string;
	subGroupName: string;
	location: Address;
}

export interface AssociationWithoutId {
	associationId?: number;
	organizationName: string;
	orgType: string;
	subGroupName: string;
	location: Pick<Address, 'city' | 'provStateRegion' | 'country'>;
}

export interface AssociationCreateRequest {
	associationId?: number;
	organizationName?: string;
	orgType?: string;
	subGroupName?: string;
	location: Pick<AddressResponse, 'city' | 'province' | 'country'>;
}

export interface AssociationResponse {
	associationId: number;
	organizationName?: string;
	orgType?: string;
	subGroupName?: string;
	location: AddressResponse;
}

export function getBaseUser(): FullUser {
	return {
		id: NULL_USER_ID,
		status: STATUS.LOADING,
		salutation: UserSalutation.NONE,
		displayName: 'Loading User',
		firstName: '',
		middleName: '',
		lastName: '',
		authenticated: false,
		verified: false,
		gender: null,
		languages: [],
		birthdayYear: '',
		birthdayMonth: '',
		birthdayDay: '',
		// Roles, etc.
		primaryRole: UserDetailRole.NONE,
		roles: [],
		credentials: [],
		specialties: [],
		interests: [],
		// Summaries
		summaryForAll: '' as HtmlString,
		summaryForContacts: '' as HtmlString,
		// Contact
		email: '',
		phone: '',
		mobile: '',
		// Occupation
		currentOccupation: {
			id: -1,
			positionTitle: '',
			startMonth: 0,
			startYear: 0,
			endMonth: null,
			endYear: null,
			phone: '',
			location: {
				street1: '',
				street2: '',
				city: '',
				provStateRegion: null,
				country: null,
				postalCode: '',
				contactInfo: {
					phone: '',
					extension: '',
					mobile: '',
					fax: '',
					webaddress: '',
				},
			},
			employer: {
				id: 0,
				name: '',
				type: '',
				logoSrc: '',
				address: {
					street1: '',
					street2: '',
					city: '',
					provStateRegion: null,
					country: null,
					postalCode: '',
					contactInfo: {
						phone: '',
						extension: '',
						mobile: '',
						fax: '',
						webaddress: '',
					},
				},
			},
		},
		professionalExperiences: [],
		associations: [],
	};
}

export interface FullUser {
	id: string;
	status: STATUS;
	// Naming...
	displayName: string;
	salutation: UserSalutation;
	firstName: string;
	middleName: string;
	lastName: string;
	// User state
	authenticated: boolean;
	verified: boolean;
	// Personal information
	gender: null | UserGender;
	languages: Language[];
	birthdayYear: string;
	birthdayMonth: string;
	birthdayDay: string;
	// Roles, etc.
	primaryRole: UserDetailRole;
	roles: UserDetailRole[];
	credentials: string[];
	specialties: string[];
	interests: string[];
	// Summaries
	summaryForAll: HtmlString;
   	summaryForContacts: HtmlString;
	// Contact
	email: string;
	phone: string;
   	mobile: string;
	// Occupation
	currentOccupation: FullUserOccupation;
	professionalExperiences: FullUserOccupation[];
	associations: Association[];
}

export interface FullUserOccupation {
	id: number;
	positionTitle: string;
	startMonth: number | null;
	startYear: number | null;
	endMonth: number | null;
	endYear: number | null;
	phone: string;
	location: AddressWithContactInfo;
	employer: {
		id: number;
		name: string;
		type: string;
		logoSrc: string;
		address: AddressWithContactInfo;
	};
}

export interface FullUserResponse {
	id: string;
	// Avatar...
	avatarSrc: string;
	// Naming...
	displayName: string;
	salutation: UserSalutation;
	firstName: string;
	middleName: string;
	lastName: string;
	// User state
	authenticated: boolean;
	verified: boolean;
	// Personal Information
	gender: null | UserGender;
	languages: null | Language[];
	birthdayYear: null | string;
	birthdayMonth: null | string;
	birthdayDay: null | string;
	// Roles, etc.
	primaryRole: null | UserDetailRoleApi;
	roles: {
		roles: UserDetailRoleApi[];
	};
	credentials: {
		credentials: string[];
	};
	specialties: {
		specialties: string[];
	};
	interests: {
		interests: string[];
	};
	// Summaries
	summaryForAll: Base64String;
   	summaryForContacts: Base64String;
	// Contact
	email: string;
	phone: string | null;
   	mobile: string | null;
	// Occupation
	currentOccupation?: FullUserOccupationResponse;
	professionalExperiences?: FullUserOccupationResponse[];
	associations?: AssociationResponse[];
}

type UpdateUserKeys = 'professionalExperiences'
	| 'summaryForAll'
	| 'summaryForContacts'
	| 'specialties'
	| 'interests'
	| 'associations'
	| 'currentOccupation';

export interface UpdateUserRequest extends Omit<FullUserResponse, UpdateUserKeys> {
	associations: AssociationCreateRequest[];
	specialties: {
		specialties: number[];
	},
	interests: {
		interests: number[];
	};
	summaryForAll: string;
	summaryForContacts: string;
	currentOccupation: Partial<FullUserOccupationRequest>;
	professionalExperiences: PartialDeep<ProfessionalExperienceRequest>[];
}

export interface ProfessionalExperienceRequest {
	id?: number;
	positionTitle: string;
	startMonth: string;
	startYear: string;
	endMonth: string;
	endYear: string;
	employer: {
		name: string;
	};
	location: {
		city: string;
		province: RegionCode | null;
		country: CountryCode | null;
	};
}

export interface FullUserOccupationRequest {
	id: number;
	positionTitle: null | string;
	startMonth: null | string;
	startYear: null | string;
	endMonth: null | string;
	endYear: null | string;
	phone: string;
	location: null | AddressWithContactInfoResponse;
	employer: {
		id: number;
	};
}

export interface FullUserOccupationResponse {
	id: number;
	positionTitle: null | string;
	startMonth: null | string;
	startYear: null | string;
	endMonth: null | string;
	endYear: null | string;
	phone: string;
	location: null | AddressWithContactInfoResponse;
	employer: {
		id: number;
		name: null | string;
		type: null | string;
		logoSrc: null | string;
		address: null | AddressWithContactInfoResponse;
	};
}

export interface UserSearchItem {
	id: string;
	salutation: UserSalutation;
	firstName: string;
	lastName: string;
	middleName: string;
	authenticated: boolean;
	verified: boolean;
	status: STATUS;
}

export interface NoteUser {
	userId: string;
	avatarSrc: string;
	salutation: UserSalutation;
	displayName: string;
	authenticated: boolean;
	verified: boolean;
}

/**
 * Group Members have special extra data, that is not part of the base user
 */
export interface GroupMember extends UserSearchItem {
	groupRole: UserRole;
	groupGrantedAccessDate: Date;
	groupJoinDate: Date;
	groupLastInviteDate: Date | null;
}

export interface GroupMemberWithEmail extends GroupMember {
	email: string;
}

export interface GroupMemberResponse extends UserSearchItem {
	groupRole: UserRole;
	groupGrantedAccessDate: string;
	groupJoinDate: string;
	groupLastInviteDate: string | null;
}

export interface GroupMemberAdminResponse extends UserSearchItem {
	groupRole: UserRole;
	groupGrantedAccessDate: string;
	groupJoinDate: string;
	groupLastInviteDate: string;
	email?: string;
}

export interface GroupMemberCountsResponse {
	member: number;
	moderator: number;
	administrator: number;
	// Below are always 0 for non-admins
	departee: number;
	invitee: number;
	externalInvitee: number;
}

export type GroupMemberCounts = Record<GroupMemberListRole, number>;

export interface UserProfilePartialOrganization {
	organizationId: number;
	name: string;
	phoneNumber: string;
	type: string;
	logoSrc: string;
}

export interface Interest {
	name: string;
	interestId: number;
}

export interface Credential {
	name: string;
	credentialId: number;
}

export interface Specialty {
	name: string;
	alias: string;
	role: UserDetailRoleApi;
	specialtyId: number;
}

export interface UserProfileOptions {
	specialties: Specialty[];
	interests: Interest[];
	credentials: Credential[];
}

export interface PartialUser extends UserSearchItem {
	displayName?: string;
	status: STATUS;
}

export enum UserAccountStatus {
	REGISTERED = 'REGISTERED',
	ACTIVATED = 'ACTIVATED',
	ACCESSED = 'ACCESSED',
	AUTHENTICATED = 'AUTHENTICATED',
	ROLE_VERIFIED = 'ROLE_VERIFIED',
	DEACTIVATED = 'DEACTIVATED',
}

export const USER_ACCOUNT_STATUSES: UserAccountStatus[] = [
	UserAccountStatus.ACCESSED,
	UserAccountStatus.ACTIVATED,
	UserAccountStatus.AUTHENTICATED,
	UserAccountStatus.DEACTIVATED,
	UserAccountStatus.REGISTERED,
	UserAccountStatus.ROLE_VERIFIED,
]

export const USER_ACCOUNT_STATUS_LABELS = {
	[UserAccountStatus.ACCESSED]: 'account.status.accessed',
	[UserAccountStatus.ACTIVATED]: 'account.status.activated',
	[UserAccountStatus.AUTHENTICATED]: 'account.status.authenticated',
	[UserAccountStatus.DEACTIVATED]: 'account.status.deactivated',
	[UserAccountStatus.REGISTERED]: 'account.status.registered',
	[UserAccountStatus.ROLE_VERIFIED]: 'account.status.role-verified',
}
